import EditNoteIcon from "@mui/icons-material/EditNote";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import { SystemPageHeader } from "../../../components/UIToolkit/molecules/SystemPageHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { usePbjPrediction } from "../hooks/usePbjPrediction";
import { usePbjTrackedPlanProgress } from "../hooks/usePbjTrackedPlan";
import { ManualHoursButton } from "../PredictionTab/ManualHoursButton";
import { PlanActionCard } from "./PlanActionCard";
import { PlanCostPanel } from "./PlanCostPanel";
import { PlanStatusHeader } from "./PlanStatusHeader";
import { formatPlanDate, isActionJudged, type PlanBasis } from "./planValue.utils";

type Props = {
	// Send the user back to the Prediction tab to assemble a different mix. The tracker itself is
	// read-only: a plan is changed by re-tracking, never by editing it here.
	onEditPlan: () => void;
};

export const PlanTab: FC<Props> = ({ onEditPlan }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { data: progress, isPending, isError } = usePbjTrackedPlanProgress(departmentId);
	// Plan-level, not per card: every action is measured over the same window, so one toggle.
	const [basis, setBasis] = useState<PlanBasis>("hours");

	// Manual hours are scoped to the PLAN's own quarter, not the page's committed range — the
	// tracker measures over the plan window, so an entry added against any other window would not
	// be the span the numbers on this tab are built from. The dialog needs a prediction snapshot to
	// preview the HPPD a logged hour moves, so one is fetched for that same window (react-query
	// caches it; the Prediction tab's own fetch uses a different key and is untouched).
	const planQuarter = useMemo(() => ({ start: progress?.quarterStart.toISODate() ?? "", end: progress?.quarterEnd.toISODate() ?? "" }), [progress]);
	const { data: quarterPrediction } = usePbjPrediction(planQuarter.start, planQuarter.end);
	// Seeded to the PLAN's start rather than the dialog's usual quarter-start. On this tab an entry
	// dated before the plan was saved is folded into the pre-plan baseline, where it raises the bar
	// instead of clearing it — so the quarter default hands a user, by default, the one span that
	// cannot show as progress. The plan start is the earliest day that can, and it covers the whole
	// measurable window rather than only from today, which would silently drop the days already
	// elapsed under the commitment.
	const manualHoursAction = quarterPrediction ? (
		<ManualHoursButton
			departmentId={departmentId}
			startDate={planQuarter.start}
			endDate={planQuarter.end}
			prediction={quarterPrediction}
			defaultPeriodStart={progress?.trackingStartDate}
		/>
	) : undefined;

	// Aggregated from the actions' own flags — the endpoint reports on-track per lever and already
	// reports false for an infeasible one, so the plan is on track only when every lever is.
	const planOnTrack = useMemo(() => (progress ? progress.actions.every((action) => action.onTrack) : false), [progress]);
	// Withheld while no week has closed anywhere in the plan. Every action's `onTrack` is vacuously
	// true until its first completed week (both sides of the comparison are zero), so aggregating them
	// in week 0 yields a guaranteed green "Plan on track" — the same absence-of-a-requirement read the
	// Investment panel already refuses to present as financial health.
	const planJudged = useMemo(() => (progress ? progress.actions.some((action) => isActionJudged(action.days)) : false), [progress]);

	useEffect(() => {
		if (!progress) return;
		track("pbj_model_tracker_viewed", {
			department_id: departmentId,
			actions_count: progress.actions.length,
			on_track: progress.actions.every((action) => action.onTrack),
		});
	}, [progress, departmentId, track]);

	if (isPending) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 6 }} data-testid="pbj-model-plan-loading">
				<CircularProgress />
			</Box>
		);
	}

	if (isError) {
		return <EmptyState icon={<WarningAmberIcon />} title={t("pbj_model.tracker.load_failed")} dataTestId="pbj-model-plan-error" />;
	}

	// A 200 with a null body is the normal "nothing tracked yet" answer, not a failure.
	if (!progress) {
		return (
			<EmptyState
				icon={<TrackChangesIcon />}
				title={t("pbj_model.tracker.empty_title")}
				hint={t("pbj_model.tracker.empty_hint")}
				action={
					<SystemButton
						buttonVariant="primary"
						onClick={onEditPlan}
						leadingIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
						data-testid="pbj-model-plan-empty-cta"
						noFullWidthMobile
					>
						{t("pbj_model.tracker.build_a_plan")}
					</SystemButton>
				}
				dataTestId="pbj-model-plan-empty"
			/>
		);
	}

	return (
		<Stack spacing={2.5} data-testid="pbj-model-plan-tab">
			{/* Same header contract as the other PBJ tabs. No "Change period" action: the tracker is
			    scoped to the PLAN's own quarter, not the page's committed range, so offering the page
			    period control here would imply it moves numbers it doesn't touch. The subtitle is not
			    "refreshes weekly": every number is live per request — only the completed-week grid that
			    `delivered` is charged over advances weekly. */}
			<SystemPageHeader
				dataTestId="pbj-model-plan-header"
				eyebrow={t("pbj_model.tabs.plan")}
				title={t("pbj_model.tracker.title")}
				subtitle={t("pbj_model.tracker.measured_through", { date: formatPlanDate(progress.elapsedThroughDate) })}
				actions={
					<>
						<SystemFlatSwitch<PlanBasis>
							options={[
								{ label: t("pbj_model.tracker.basis_hours"), value: "hours", "data-testid": "pbj-model-plan-basis-hours" },
								{ label: t("pbj_model.tracker.basis_hppd"), value: "hppd", "data-testid": "pbj-model-plan-basis-hppd" },
							]}
							activeIndexOrValue={basis}
							onSwitch={(option) => setBasis(option.value)}
							delayCallback={0}
							equalWidthOptions
							data-testid="pbj-model-plan-basis-switch"
						/>
						{/* Lives here rather than on a banner: entering manual hours is always a valid thing to
					    do from this tab — to correct the levels, to replace an estimate, or to edit what is
					    already booked — and the banners it used to hang off both disappear once a
					    department has entered its hours, taking the only way in with them. */}
						{manualHoursAction}
						<SystemButton
							buttonVariant="primary"
							onClick={onEditPlan}
							leadingIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
							data-testid="pbj-model-plan-edit"
							noFullWidthMobile
						>
							{t("pbj_model.tracker.edit_plan")}
						</SystemButton>
					</>
				}
			/>

			{/* Plan adherence and the CMS score are measured over different spans, so they disagree by
			    design — the pre-plan portion of the quarter still counts toward the score. */}
			<SystemBanner
				tone="info"
				icon={<InfoOutlinedIcon />}
				title={t("pbj_model.tracker.accuracy_banner.title")}
				description={t("pbj_model.tracker.accuracy_banner.body", { date: formatPlanDate(progress.trackingStartDate) })}
				dataTestId="pbj-model-plan-accuracy-banner"
			/>
			{!progress.baselineAvailable && (
				<SystemBanner
					tone="warning"
					icon={<WarningAmberIcon />}
					title={t("pbj_model.tracker.no_baseline_banner.title")}
					description={t("pbj_model.tracker.no_baseline_banner.body")}
					dataTestId="pbj-model-plan-no-baseline-banner"
				/>
			)}
			{/* Manual hours are logged at the end of a quarter, so mid-quarter these numbers can be
			    built on a fraction of the hours the department will eventually report. An estimate
			    filled in for the uncovered days is worth naming; so is having neither an estimate nor
			    an entry. What must NOT happen is telling a department that has booked its hours that
			    they are missing — `estimateIncluded` is false both when nothing was estimated and
			    when every applicable day was already covered by a real entry, so it cannot carry that
			    judgement alone and `manualHours` is what separates the two. */}
			{progress.estimateIncluded ? (
				<SystemBanner
					tone="info"
					icon={<FunctionsIcon />}
					title={t("pbj_model.tracker.estimate_banner.title", {
						hours: (progress.estimatedHours ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 }),
					})}
					description={t("pbj_model.tracker.estimate_banner.body", { quarter: progress.estimateSourceQuarter ?? "" })}
					dataTestId="pbj-model-plan-estimate-banner"
				/>
			) : (
				progress.manualHours === null && (
					<SystemBanner
						tone="warning"
						icon={<EditNoteIcon />}
						title={t("pbj_model.tracker.manual_hours_banner.title")}
						description={t("pbj_model.tracker.manual_hours_banner.body")}
						dataTestId="pbj-model-plan-manual-hours-banner"
					/>
				)
			)}

			<Stack direction={{ xs: "column", lg: "row" }} spacing={3} sx={{ alignItems: "stretch" }}>
				<Box sx={{ flex: 2, minWidth: 0 }}>
					<PlanStatusHeader
						status={progress.status}
						onTrack={planOnTrack}
						judged={planJudged}
						quarterStart={progress.quarterStart}
						quarterEnd={progress.quarterEnd}
						trackingStartDate={progress.trackingStartDate}
					/>
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<PlanCostPanel cost={progress.cost} />
				</Box>
			</Stack>

			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
					{t("pbj_model.tracker.actions_title")}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }} data-testid="pbj-model-plan-weeks">
					{t("pbj_model.tracker.week_of", { elapsed: progress.planWeeks.elapsed, total: progress.planWeeks.total })}
				</Typography>
			</Stack>
			<Stack spacing={2}>
				{progress.actions.map((action) => (
					<PlanActionCard key={action.id} action={action} basis={basis} weeksRemaining={progress.planWeeks.remaining} />
				))}
			</Stack>
		</Stack>
	);
};
